import { BaseGame } from "@/components/app/base-game";
import { OfferInBundle } from "@/components/app/bundle-game";
import { FranchiseBanner } from "@/components/app/franchise-banner";
import { InternalBanner } from "@/components/app/internal-banner";
import { OfferHero } from "@/components/app/offer-hero";
import { SectionsNav } from "@/components/app/offer-sections";
import { OpenLauncher } from "@/components/app/open-launcher";
import { platformIcons } from "@/components/app/platform-icons";
import { PrepurchasePopup } from "@/components/app/prepurchase-popup";
import { StoreDropdown } from "@/components/app/store-dropdown";
import { AddIcon } from "@/components/icons/add";
import { RemoveIcon } from "@/components/icons/remove";
import { Bundle } from "@/components/modules/bundle";
import { CollectionOffers } from "@/components/modules/collection-offers";
import { SellerOffers } from "@/components/modules/seller-offers";
import { SuggestedOffers } from "@/components/modules/suggested-offers";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { useCompare } from "@/hooks/use-compare";
import { useCountry } from "@/hooks/use-country";
import { useLocale } from "@/hooks/use-locale";
import { usePushNotifications } from "@/hooks/use-push-notifications";
import { ClientOnly } from "@/lib/cllient-only";
import { Seller } from "@/lib/get-seller";
import { offerGqlQueryOptions } from "@/queries/offer-gql";
import { internalNamespaces } from "@/lib/internal-namespaces";
import { offersDictionary } from "@/lib/offers-dictionary";
import { compareDates, timeAgo } from "@/lib/time-ago";
import { cn } from "@/lib/utils";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { Link } from "@/components/app/localized-link";
import { FabIcon } from "@/components/icons/fab";
import { DateTime } from "luxon";
import { Bell } from "lucide-react";
import { Suspense } from "react";
import { useCookies } from "react-cookie";
import { OffersHomeSkeleton } from "@/components/skeletons/offers-home";
import { TechnologyLink } from "@/components/technology/TechnologyLink";
import { useTranslation } from "@/lib/paraglide-react";
import { Route } from "/app/src/routes/{-$locale}/offers/$id.tsx";
function OfferPage() {
  const {
    t
  } = useTranslation();
  const {
    id
  } = Route.useParams();
  const {
    locale,
    timezone
  } = useLocale();
  const {
    country
  } = useCountry();
  const {
    addToCompare,
    removeFromCompare,
    compare
  } = useCompare();
  const navigate = useNavigate();
  const location = useLocation();
  // Auth and push notifications
  const [cookies] = useCookies(["push-notifications-api-key"]);
  const {
    canPerformTopicOperations,
    subscribeToTopic,
    unsubscribeFromTopic,
    isSubscribedToTopic
  } = usePushNotifications(cookies["push-notifications-api-key"]);

  // Topic names for this offer
  const allTopic = `offer:${id}:*`;
  const priceTopic = `offer:${id}:price`;
  const achievementsTopic = `offer:${id}:achievements`;
  const buildsTopic = `offer:${id}:builds`;
  const itemsTopic = `offer:${id}:items`;
  const requirementsTopic = `offer:${id}:requirements`;
  const metadataTopic = `offer:${id}:metadata`;
  const mediaTopic = `offer:${id}:media`;

  // Check current subscription status
  const all = isSubscribedToTopic(allTopic);
  const price = isSubscribedToTopic(priceTopic);
  const achievements = isSubscribedToTopic(achievementsTopic);
  const builds = isSubscribedToTopic(buildsTopic);
  const items = isSubscribedToTopic(itemsTopic);
  const requirements = isSubscribedToTopic(requirementsTopic);
  const metadata = isSubscribedToTopic(metadataTopic);
  const media = isSubscribedToTopic(mediaTopic);

  // Handle notification setting changes
  const handleNotificationChange = async (setting: string, checked: boolean) => {
    if (!canPerformTopicOperations) return;
    const topicMap = {
      all: allTopic,
      price: priceTopic,
      achievements: achievementsTopic,
      builds: buildsTopic,
      items: itemsTopic,
      requirements: requirementsTopic,
      metadata: metadataTopic,
      media: mediaTopic
    };
    const specificTopics = [priceTopic, achievementsTopic, buildsTopic, itemsTopic, requirementsTopic, metadataTopic, mediaTopic];
    if (setting === "all") {
      if (checked) {
        // Subscribe to all changes and unsubscribe from specific ones
        await subscribeToTopic(allTopic);
        for (const topic of specificTopics) {
          if (isSubscribedToTopic(topic)) {
            await unsubscribeFromTopic(topic);
          }
        }
      } else {
        // Unsubscribe from all changes
        await unsubscribeFromTopic(allTopic);
      }
    } else {
      const topic = topicMap[setting as keyof typeof topicMap];
      if (topic) {
        if (checked) {
          // Unsubscribe from all changes first, then subscribe to specific topic
          if (isSubscribedToTopic(allTopic)) {
            await unsubscribeFromTopic(allTopic);
          }
          await subscribeToTopic(topic);
        } else {
          await unsubscribeFromTopic(topic);
        }
      }
    }
  };
  const {
    data,
    isLoading: offerLoading
  } = useSuspenseQuery(offerGqlQueryOptions({
    id,
    country: country || "US",
    locale
  }));
  const offer = data?.offer;
  const technologies = data?.technologies ?? [];
  const franchises = data?.franchises ?? [];
  const franchise = franchises[0];
  const subPath = location.pathname.split(`/${id}/`)[1];
  if (offerLoading) {
    return <OffersHomeSkeleton />;
  }
  if (!offer) {
    return <main>{t("offerDetail.common.offerNotFound")}</main>;
  }
  if (offer.title === "Error") {
    return <main>{offer.description}</main>;
  }
  const isFabItem = offer.customAttributes.FabListingId;
  const expiryDateValue = offer.expiryDate;
  const expiryDate = expiryDateValue ? DateTime.fromISO(expiryDateValue).toUTC() : null;
  const isUnavailableForPurchase = Boolean(expiryDate?.isValid && expiryDate < DateTime.utc());
  const platformTags = offer.tags.filter(tag => tag !== null).filter(tag => platformIcons[tag.id]);
  return <main className="flex flex-col items-start justify-start w-full min-h-screen gap-4 relative px-4 md:px-8 max-w-[1600px] mx-auto">
      <ClientOnly>
        <PrepurchasePopup id={offer.id} />
      </ClientOnly>
      <header className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-1">
          <div className="inline-flex min-w-0 flex-wrap items-center gap-3 md:gap-4">
            {isFabItem && <FabIcon className="size-10" />}
            <h1 className="min-w-0 text-2xl font-bold leading-tight md:text-4xl">{offer.title}</h1>
          </div>
          <h4 className="inline-flex flex-wrap items-center gap-2 text-base font-semibold opacity-50 md:text-lg" aria-label={t("offerDetail.hero.offeredBy", {
          seller: offer.seller.name
        })}>
            <Seller developerDisplayName={offer.developerDisplayName as string} publisherDisplayName={offer.publisherDisplayName as string} seller={offer.seller.name} customAttributes={offer.customAttributes} />
            {offer.prePurchase && <Badge variant="outline">{t("offerDetail.badges.prePurchase")}</Badge>}
            {isUnavailableForPurchase && <Badge variant="destructive">{t("offerDetail.badges.unavailableForPurchase")}</Badge>}
            {offer.tags.find(tag => tag.id === "1310") && <Badge variant="outline">{t("offerDetail.badges.earlyAccess")}</Badge>}
          </h4>

          <div className="mt-2 overflow-hidden rounded-xl border border-border/10">
            <Table className="min-w-[640px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px] md:w-[300px]">
                    {t("offerDetail.table.offerId")}
                  </TableHead>
                  <TableHead className="text-left font-mono border-l-border/10 border-l">
                    {offer.id}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">{t("offerDetail.table.namespace")}</TableCell>
                  <TableCell className={"text-left font-mono border-l-border/10 border-l underline decoration-dotted decoration-border underline-offset-4"}>
                    <Link to={"/{-$locale}/sandboxes/$id"} params={{
                    id: offer.namespace
                  }}>
                      {internalNamespaces.includes(offer.namespace) ? <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>{offer.namespace}</TooltipTrigger>
                            <TooltipContent>
                              <p>{t("offerDetail.tooltip.internalNamespace")}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider> : offer.namespace}
                    </Link>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">{t("offerDetail.table.offerType")}</TableCell>
                  <TableCell className="text-left border-l-border/10 border-l">
                    {offersDictionary[offer.offerType as keyof typeof offersDictionary] || offer.offerType}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">{t("offerDetail.table.seller")}</TableCell>
                  <TableCell className="text-left border-l-border/10 border-l">
                    <Link to="/{-$locale}/sellers/$id" params={{
                    id: offer.seller.id
                  }} className="underline underline-offset-4">
                      {offer.seller.name}
                    </Link>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">{t("offerDetail.table.developer")}</TableCell>
                  <TableCell className="text-left inline-flex items-center gap-1 border-l-border/10 border-l">
                    <Seller developerDisplayName={offer.developerDisplayName as string} publisherDisplayName={offer.publisherDisplayName as string} seller={offer.seller.name} customAttributes={offer.customAttributes} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">
                    {t("offerDetail.table.supportedPlatforms")}
                  </TableCell>
                  <TableCell className="text-left border-l-border/10 border-l inline-flex items-center justify-start gap-1">
                    {platformTags.map(tag => <span key={tag.id} className="text-sm">
                        {platformIcons[tag.id]}
                      </span>)}

                    {!platformTags.length && <div>{t("offerDetail.common.na")}</div>}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">
                    {t("offerDetail.table.releaseDate")}
                  </TableCell>
                  <TableCell className="text-left inline-flex items-center gap-1 border-l-border/10 border-l">
                    <ReleaseDate releaseDate={offer.releaseDate} pcReleaseDate={offer.pcReleaseDate} timezone={timezone} />
                  </TableCell>
                </TableRow>
                {expiryDateValue && expiryDate && <TableRow>
                    <TableCell className="font-medium">
                      {t("offerDetail.table.expirationDate")}
                    </TableCell>
                    <TableCell className="text-left inline-flex items-center gap-1 border-l-border/10 border-l">
                      {expiryDate.setZone(timezone).setLocale("en-GB").toLocaleString({
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                    timeZoneName: "short"
                  })}
                      <TimeAgo targetDate={expiryDateValue} />
                    </TableCell>
                  </TableRow>}
                <TableRow>
                  <TableCell className="font-medium">{t("offerDetail.table.lastUpdate")}</TableCell>
                  <TableCell className="text-left inline-flex items-center gap-1 border-l-border/10 border-l">
                    {offer.lastModifiedDate ? DateTime.fromISO(offer.lastModifiedDate, {
                    zone: timezone
                  }).setLocale("en-GB").toLocaleString({
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                    timeZoneName: "short"
                  }) : t("offerDetail.common.notAvailable")}
                    <TimeAgo targetDate={offer.lastModifiedDate} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">
                    {t("offerDetail.table.creationDate")}
                  </TableCell>
                  <TableCell className="text-left inline-flex items-center gap-1 border-l-border/10 border-l">
                    {offer.creationDate ? DateTime.fromISO(offer.creationDate, {
                    zone: timezone
                  }).setLocale("en-GB").toLocaleString({
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                    timeZoneName: "short"
                  }) : t("offerDetail.common.notAvailable")}
                    <TimeAgo targetDate={offer.creationDate} />
                  </TableCell>
                </TableRow>
                {technologies && technologies.length > 0 && <TableRow>
                    <TableCell className="font-medium">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <span className="underline decoration-dotted decoration-border/60 underline-offset-4 cursor-help">
                              {t("offerDetail.table.technologies")}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-sm max-w-md">
                              {t("offerDetail.tooltip.techSource")}{" "}
                              <a href="https://steamdb.info/tech/" className="text-blue-700 font-semibold" target="_blank" rel="noopener noreferrer">
                                {t("offerDetail.tooltip.techSourceSteamDb")}
                              </a>{" "}
                              {t("offerDetail.tooltip.techDescription")}
                              <br />
                              <a href="https://github.com/SteamDatabase/FileDetectionRuleSets" className="text-blue-700 font-semibold" target="_blank" rel="noopener noreferrer">
                                {t("offerDetail.tooltip.source")}
                              </a>
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell className="text-left border-l-border/10 border-l flex flex-wrap flex-row gap-1">
                      {technologies.filter(technology => technology.section === "Engine" || technology.section === "AntiCheat" || technology.section === "Container" || technology.section === "Emulator" || technology.section === "Launcher" || technology.technology === "SteamworksNET" || technology.technology === "EpicOnlineServices" || technology.technology === "Steam_Networking")
                  // Sort by
                  // Engine
                  // Launcher
                  // Anti-cheat
                  .sort((a, b) => {
                    if (a.section === "Engine") {
                      return -1;
                    }
                    if (b.section === "Engine") {
                      return 1;
                    }
                    if (a.section === "Launcher") {
                      return -1;
                    }
                    if (b.section === "Launcher") {
                      return 1;
                    }
                    if (a.section === "AntiCheat") {
                      return -1;
                    }
                    if (b.section === "AntiCheat") {
                      return 1;
                    }
                    return 0;
                  }).map((technology, index, array) => <span key={`${technology.section}-${technology.technology}`} className="font-mono">
                            <TechnologyLink id={technology.technology} className="underline-offset-4 hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                              {technology.technology}
                            </TechnologyLink>
                            <sup className="ml-[2px] text-[0.68rem]">{technology.section}</sup>
                            {index < array.length - 1 && ","}
                          </span>)}
                    </TableCell>
                  </TableRow>}
              </TableBody>
            </Table>
          </div>
          <InternalBanner offer={offer} />
          <BaseGame offer={offer} />
          <OfferInBundle offer={offer} />
        </div>
        <div className="flex justify-start items-start flex-col gap-4">
          <div className="flex h-auto w-full flex-wrap items-center justify-start gap-2 md:h-8 md:justify-end">
            <StoreDropdown offer={offer} />
            <OpenLauncher id={offer.id} />
            <Button onClick={() => {
            if (compare.includes(offer.id)) {
              removeFromCompare(offer.id);
            } else {
              addToCompare(offer.id);
            }
          }} className="inline-flex items-center gap-1 bg-card text-card-foreground hover:bg-accent border border-border/60">
              {compare.includes(offer.id) ? <RemoveIcon /> : <AddIcon />}
              <span>{t("offerDetail.actions.compare")}</span>
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button className="inline-flex items-center gap-1 bg-card text-card-foreground hover:bg-accent border border-border/60" size="sm">
                  <Bell className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80">
                <div className="space-y-4">
                  <h4 className="font-medium leading-none">
                    {t("offerDetail.notifications.title")}
                  </h4>
                  {canPerformTopicOperations ? <>
                      <p className="text-sm text-muted-foreground">
                        {t("offerDetail.notifications.description")}
                      </p>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <Checkbox id="all" checked={all} onCheckedChange={checked => handleNotificationChange("all", checked as boolean)} disabled={price || achievements || builds || items || requirements || metadata || media} />
                          <label htmlFor="all" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            {t("offerDetail.notifications.all")}
                          </label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox id="price" checked={price} onCheckedChange={checked => handleNotificationChange("price", checked as boolean)} disabled={all} />
                          <label htmlFor="price" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            {t("offerDetail.notifications.price")}
                          </label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox id="achievements" checked={achievements} onCheckedChange={checked => handleNotificationChange("achievements", checked as boolean)} disabled={all} />
                          <label htmlFor="achievements" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            {t("offerDetail.notifications.achievements")}
                          </label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox id="builds" checked={builds} onCheckedChange={checked => handleNotificationChange("builds", checked as boolean)} disabled={all} />
                          <label htmlFor="builds" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            {t("offerDetail.notifications.builds")}
                          </label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox id="items" checked={items} onCheckedChange={checked => handleNotificationChange("items", checked as boolean)} disabled={all} />
                          <label htmlFor="items" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            {t("offerDetail.notifications.items")}
                          </label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox id="requirements" checked={requirements} onCheckedChange={checked => handleNotificationChange("requirements", checked as boolean)} disabled={all} />
                          <label htmlFor="requirements" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            {t("offerDetail.notifications.requirements")}
                          </label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox id="metadata" checked={metadata} onCheckedChange={checked => handleNotificationChange("metadata", checked as boolean)} disabled={all} />
                          <label htmlFor="metadata" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            {t("offerDetail.notifications.metadata")}
                          </label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox id="media" checked={media} onCheckedChange={checked => handleNotificationChange("media", checked as boolean)} disabled={all} />
                          <label htmlFor="media" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            {t("offerDetail.notifications.media")}
                          </label>
                        </div>
                      </div>
                    </> : <>
                      <p className="text-sm text-muted-foreground">
                        {t("offerDetail.notifications.enablePushDescription")}
                      </p>
                      <Button asChild className="w-full">
                        <Link to="/{-$locale}/notifications">
                          {t("offerDetail.notifications.enablePushAction")}
                        </Link>
                      </Button>
                    </>}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <OfferHero offer={offer} />
          {franchise && franchise.offers.length > 1 && franchise.namespaces.length > 1 && <FranchiseBanner franchise={franchise} />}
          <p className="px-1">{offer.description}</p>
        </div>
      </header>

      {offer.categories.findIndex(category => category === "collections") !== -1 && <section className="w-full min-h-[50vh] flex flex-col gap-4">
          <CollectionOffers id={offer.id} />
        </section>}

      {offer.offerType === "BUNDLE" || offer.offerType === "Bundle" ? <section className="w-full min-h-[50vh] flex flex-col gap-4">
          <hr className="my-4" />
          <Bundle id={offer.id} offer={offer} />
          <hr className="my-4" />
        </section> : null}

      <section id="offer-information" className="w-full min-h-[50vh] flex flex-col gap-4">
        <SectionsNav links={[{
        id: "",
        label: t("offerDetail.tabs.overview"),
        href: `/offers/${offer.id}`
      }, {
        id: "price",
        label: t("offerDetail.tabs.price"),
        href: `/offers/${offer.id}/price`
      }, {
        id: "items",
        label: t("offerDetail.tabs.items"),
        href: `/offers/${offer.id}/items`
      }, {
        id: "builds",
        label: t("offerDetail.tabs.builds"),
        href: `/offers/${offer.id}/builds`
      }, {
        id: "achievements",
        label: t("offerDetail.tabs.achievements"),
        href: `/offers/${offer.id}/achievements`
      }, {
        id: "related",
        label: t("offerDetail.tabs.related"),
        href: `/offers/${offer.id}/related`
      }, {
        id: "metadata",
        label: t("offerDetail.tabs.metadata"),
        href: `/offers/${offer.id}/metadata`
      }, {
        id: "changelog",
        label: t("offerDetail.tabs.changelog"),
        href: `/offers/${offer.id}/changelog`
      }, {
        id: "media",
        label: t("offerDetail.tabs.media"),
        href: `/offers/${offer.id}/media`
      }, {
        id: "reviews",
        label: t("offerDetail.tabs.reviews"),
        href: `/offers/${offer.id}/reviews`
      }]} activeSection={subPath ?? ""} onSectionChange={id => {
        const offerRoutes = {
          "": "/{-$locale}/offers/$id",
          price: "/{-$locale}/offers/$id/price",
          items: "/{-$locale}/offers/$id/items",
          builds: "/{-$locale}/offers/$id/builds",
          achievements: "/{-$locale}/offers/$id/achievements",
          related: "/{-$locale}/offers/$id/related",
          metadata: "/{-$locale}/offers/$id/metadata",
          changelog: "/{-$locale}/offers/$id/changelog",
          media: "/{-$locale}/offers/$id/media",
          reviews: "/{-$locale}/offers/$id/reviews"
        } as const;
        const to = offerRoutes[id as keyof typeof offerRoutes] ?? offerRoutes[""];
        navigate({
          to,
          params: {
            id: offer.id
          },
          replace: false,
          resetScroll: false
        });
      }} />

        <div className="mx-auto w-full max-w-7xl px-4">
          <Outlet />
        </div>
      </section>

      <hr className="my-4 border-border/40" />

      <SellerOffers id={offer.seller.id} name={offer.seller.name} currentOffer={offer} />

      <SuggestedOffers id={offer.id} />
    </main>;
}
const TimeAgo: React.FC<{
  targetDate: string;
}> = ({
  targetDate
}) => {
  const {
    t
  } = useTranslation();
  return <span className="opacity-50">
      ({targetDate ? timeAgo(new Date(targetDate)) : t("offerDetail.common.notAvailable")})
    </span>;
};
const ReleaseDate: React.FC<{
  releaseDate: string | null;
  pcReleaseDate: string | null;
  timezone: string | undefined;
}> = ({
  releaseDate,
  pcReleaseDate,
  timezone
}) => {
  const {
    t
  } = useTranslation();
  if (!releaseDate || releaseDate.includes("2099")) {
    return <span>{t("offerDetail.common.notAvailable")}</span>;
  }
  return <>
      <TooltipProvider>
        <Tooltip open={!pcReleaseDate ? false : undefined}>
          <TooltipTrigger className="flex items-center gap-1 cursor-default">
            <span className={cn(pcReleaseDate && releaseDate !== pcReleaseDate && "underline decoration-dotted underline-offset-4")}>
              {DateTime.fromISO(releaseDate).setZone(timezone || "UTC").setLocale("en-GB").toLocaleString({
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "numeric",
              minute: "numeric",
              timeZoneName: "short"
            })}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {!pcReleaseDate || releaseDate === pcReleaseDate ? t("offerDetail.releaseDate.releasedSameDay") : <span>
                <span>{t("offerDetail.releaseDate.releasedOnPc")} </span>
                <span>{compareDates(new Date(pcReleaseDate), new Date(releaseDate))}</span>
                <span>{t("offerDetail.releaseDate.theEgs")}</span>
              </span>}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <TimeAgo targetDate={releaseDate} />
    </>;
};
const SplitComponent = () => {
  return <Suspense fallback={<OffersHomeSkeleton />}>
        <OfferPage />
      </Suspense>;
};
export { SplitComponent as component };